import { forwardRef, type ButtonHTMLAttributes } from 'react'
import { cn } from '@/lib/cn'

type Variant = 'metal' | 'blood' | 'ghost' | 'gold'
type Size = 'sm' | 'md' | 'lg'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  size?: Size
  /** Red danger pulse (Style Guide: "red pulse for danger"). */
  danger?: boolean
}

const variants: Record<Variant, string> = {
  metal: 'btn-metal',
  blood:
    'inline-flex items-center justify-center gap-2 rounded-sm border border-blood-400/60 bg-gradient-to-b from-blood-600 to-blood-800 text-bone uppercase tracking-wide font-ui hover:shadow-glow-blood hover:border-blood-300 transition-all',
  gold: 'inline-flex items-center justify-center gap-2 rounded-sm border border-gold bg-gradient-to-b from-gold/90 to-gold/60 text-obsidian font-ui uppercase tracking-wide hover:shadow-glow-gold transition-all',
  ghost:
    'inline-flex items-center justify-center gap-2 rounded-sm border border-transparent text-bone-muted hover:text-bone hover:border-gold/30 font-ui uppercase tracking-wide transition-colors',
}

const sizes: Record<Size, string> = {
  sm: 'px-3 py-1.5 text-xs',
  md: 'px-4 py-2 text-sm',
  lg: 'px-6 py-3 text-base',
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'metal', size = 'md', danger, className, children, ...rest }, ref) => (
    <button
      ref={ref}
      className={cn(variants[variant], sizes[size], danger && 'animate-pulse-blood', 'disabled:cursor-not-allowed disabled:opacity-50', className)}
      {...rest}
    >
      {children}
    </button>
  ),
)
Button.displayName = 'Button'
